// @ts-nocheck
import { supabase } from "@/integrations/supabase/client";

export type SheetScope = "event" | "payment" | "roster";
export type Permission = "view" | "comment" | "edit";

export const RPC_BY_SCOPE: Record<SheetScope, string> = {
  event: "get_event_sheet_data",
  payment: "get_payment_sheet_data",
  roster: "get_roster_sheet_data",
};

export interface SheetColumn {
  key: string;
  label: string;
  field?: string; // event_registrations_v2 / event_rosters column to write
  type?: "text" | "number" | "currency" | "select" | "bool" | "date" | "datetime";
  options?: { value: string; label: string }[];
  editable?: boolean;
  width?: string;
}

const PAYMENT_STATUS_OPTIONS = [
  { value: "unpaid", label: "Unpaid" },
  { value: "pending", label: "Pending" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "comped", label: "Comped" },
  { value: "refunded", label: "Refunded" },
  { value: "partially_refunded", label: "Partially refunded" },
];

const REG_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "waitlisted", label: "Waitlisted" },
  { value: "cancelled", label: "Cancelled" },
  { value: "withdrawn", label: "Withdrawn" },
];

export const EVENT_COLUMNS: SheetColumn[] = [
  { key: "division", label: "Div", field: "division", type: "text", editable: true, width: "w-20" },
  { key: "pool", label: "Pool", field: "pool", type: "text", editable: true, width: "w-16" },
  { key: "team_name", label: "Team Name", type: "text", editable: false },
  { key: "coach_first", label: "Coach First", type: "text", editable: false },
  { key: "coach_last", label: "Coach Last", type: "text", editable: false },
  { key: "email", label: "Email", field: "primary_billing_email", type: "text", editable: true },
  { key: "phone", label: "Phone", field: "coach_phone", type: "text", editable: true },
  { key: "created_at", label: "Registered", type: "date", editable: false },
  { key: "payment_status", label: "Paid?", field: "payment_status", type: "select", options: PAYMENT_STATUS_OPTIONS, editable: true },
  { key: "total", label: "Total", field: "total_amount_due", type: "currency", editable: true },
  { key: "paid", label: "Paid $", field: "total_amount_paid", type: "currency", editable: true },
  { key: "owed", label: "Owed $", type: "currency", editable: false },
  { key: "pay_type", label: "Pay Type", field: "payment_method_chosen", type: "text", editable: true },
  { key: "roster_player_count", label: "Roster", type: "number", editable: false },
];

export const PAYMENT_COLUMNS: SheetColumn[] = [
  { key: "division", label: "Div", type: "text", editable: false, width: "w-20" },
  { key: "team_name", label: "Team Name", type: "text", editable: false },
  { key: "payment_status", label: "Status", field: "payment_status", type: "select", options: PAYMENT_STATUS_OPTIONS, editable: true },
  { key: "total", label: "Total", field: "total_amount_due", type: "currency", editable: true },
  { key: "paid", label: "Paid $", field: "total_amount_paid", type: "currency", editable: true },
  { key: "refunded", label: "Refunded $", type: "currency", editable: false },
  { key: "owed", label: "Owed $", type: "currency", editable: false },
  { key: "scheduling_tier_fee", label: "Tier Fee", field: "scheduling_tier_fee", type: "currency", editable: true },
  { key: "pay_type", label: "Pay Type", field: "payment_method_chosen", type: "text", editable: true },
  { key: "payment_received_method", label: "Received Via", type: "text", editable: false },
  { key: "payment_received_at", label: "Received At", type: "datetime", editable: false },
  { key: "payment_due_at", label: "Due", type: "date", editable: false },
];

export const ROSTER_COLUMNS: SheetColumn[] = [
  { key: "team_name", label: "Team", type: "text", editable: false },
  { key: "jersey_number", label: "#", field: "jersey_number", type: "text", editable: true, width: "w-16" },
  { key: "player_name", label: "Player", type: "text", editable: false },
  { key: "grad_year", label: "Grad", type: "text", editable: false, width: "w-20" },
  { key: "position", label: "Position", field: "position", type: "text", editable: true },
  { key: "is_captain", label: "Captain", field: "is_captain", type: "bool", editable: true },
  { key: "is_guest_player", label: "Guest", field: "is_guest_player", type: "bool", editable: true },
  { key: "claim_status", label: "Claim", type: "text", editable: false },
];

export const ROSTER_CHECKIN_COLUMN: SheetColumn = {
  key: "checked_in_at", label: "Checked In", field: "checked_in_at", type: "bool", editable: true,
};

export function columnsForScope(scope: SheetScope, showCheckin = false): SheetColumn[] {
  if (scope === "payment") return PAYMENT_COLUMNS;
  if (scope === "roster") return showCheckin ? [...ROSTER_COLUMNS, ROSTER_CHECKIN_COLUMN] : ROSTER_COLUMNS;
  return EVENT_COLUMNS;
}

// ---- viewer identity (localStorage, no auth) ----
export function getViewer() {
  return {
    name: localStorage.getItem("sheet_viewer_name") || "",
    email: localStorage.getItem("sheet_viewer_email") || "",
  };
}
export function setViewer(name: string, email: string) {
  localStorage.setItem("sheet_viewer_name", name);
  localStorage.setItem("sheet_viewer_email", email.toLowerCase().trim());
}

// ---- data fetch / mutate ----
export async function fetchSheet(scope: SheetScope, token: string) {
  const { data, error } = await (supabase as any).rpc(RPC_BY_SCOPE[scope], { p_token: token });
  if (error) throw error;
  return data;
}

export async function writeCell(scope: SheetScope, token: string, rowId: string, field: string, value: any) {
  const rpc = scope === "roster" ? "update_sheet_roster_cell" : "update_sheet_cell";
  const idArg = scope === "roster" ? { p_roster_id: rowId } : { p_reg_id: rowId };
  const { data, error } = await (supabase as any).rpc(rpc, {
    p_token: token,
    ...idArg,
    p_field: field,
    p_value: value == null ? "" : String(value),
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function addSheetNote(opts: {
  registrationId: string;
  eventId: string;
  tokenId: string;
  authorName: string;
  note: string;
}) {
  const { error } = await (supabase as any).from("event_sheet_notes").insert({
    registration_id: opts.registrationId,
    event_id: opts.eventId,
    token_id: opts.tokenId,
    author_name: opts.authorName,
    note: opts.note,
  });
  if (error) throw error;
}

// ---- presence color ----
const PRESENCE_COLORS = [
  "#002E62", "#BC8D09", "#0f766e", "#9333ea", "#dc2626", "#2563eb", "#ea580c", "#16a34a",
];
export function colorForEmail(email: string) {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) >>> 0;
  return PRESENCE_COLORS[h % PRESENCE_COLORS.length];
}
export function initials(name: string, email: string) {
  const base = (name || email || "?").trim();
  const parts = base.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

// ---- CSV ----
export function fmtCellForCsv(row: any, col: SheetColumn) {
  const v = row[col.key];
  if (v == null) return "";
  if (col.type === "bool") return v ? "Yes" : "No";
  return String(v);
}
export function buildCsv(columns: SheetColumn[], rows: any[]) {
  const esc = (s: string) => `"${String(s).replace(/"/g, '""')}"`;
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => esc(fmtCellForCsv(r, c))).join(","))
    .join("\n");
  return `${header}\n${body}`;
}
export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
